import Joi, { MAX_CHARACTERS, uuidv4 } from '../../../helpers/joiCommonSchemas';
import { validEmail } from '../../../helpers/joiCommonSchemas';
import {
  singleSelectDropdownOption,
  singleSelectDropdownOptionWithVtRequirements,
} from '../../../shared/schemas/dropdown';

export const VALIDATION_MESSAGE = {
  FIRST_NAME_MISSING: 'Please enter your first name',
  FIRST_NAME_MIN_LENGTH: 'First name is too short',
  FIRST_NAME_MAX_LENGTH: 'First name is too long',

  LAST_NAME_MISSING: 'Please enter your last name',
  LAST_NAME_MIN_LENGTH: 'Last name is too short',
  LAST_NAME_MAX_LENGTH: 'Last name is too long',

  EMAIL_MISSING: 'Please enter a valid email',

  MOBILE_MISSING: 'Please enter a valid mobile number',
  MOBILE_INCORRECT: 'Please enter a valid mobile number',

  EMAIL_OR_MOBILE_REQUIRED: 'Email or mobile required',

  COMPANY_MISSING: 'Please enter your company name',
  COMPANY_MIN_LENGTH: 'Company name is too short',
  COMPANY_MAX_LENGTH: 'Company name is too long',
};

const firstNameSchema = () =>
  Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGE.FIRST_NAME_MISSING,
    'string.min': VALIDATION_MESSAGE.FIRST_NAME_MIN_LENGTH,
    'string.max': VALIDATION_MESSAGE.FIRST_NAME_MAX_LENGTH,
  });

const lastNameSchema = () =>
  Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGE.LAST_NAME_MISSING,
    'string.min': VALIDATION_MESSAGE.LAST_NAME_MIN_LENGTH,
    'string.max': VALIDATION_MESSAGE.LAST_NAME_MAX_LENGTH,
  });

const companySchema = () =>
  Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGE.COMPANY_MISSING,
    'string.max': VALIDATION_MESSAGE.COMPANY_MAX_LENGTH,
  });

const companyIdSchema = () => uuidv4().optional().allow(null);

const companySourceSchema = () =>
  Joi.string().trim().valid('new', 'global', 'verified').allow(null).messages({
    'any.only': 'Value must be one of ["new", "global", "verified"]',
  });

const mobileSchema = () => Joi.string().phoneNumber();

function optionalField(schema) {
  return schema.allow('').optional();
}

function isEmailOrMobileRequired(schema) {
  return schema.append({
    mobile: Joi.alternatives().conditional('email', {
      is: '',
      then: mobileSchema().messages({
        'string.empty': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
        'string.phoneNumber': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
      }),
      otherwise: optionalField(mobileSchema()),
    }),
    email: Joi.alternatives().conditional('mobile', {
      is: '',
      then: validEmail().messages({
        'string.empty': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
        'string.email': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
      }),
      otherwise: optionalField(validEmail()),
    }),
  });
}

function notEmailOrMobileRequired(
  schema,
  emailRequired,
  mobileRequired,
  visitorTypesDetailsRequired,
  detailsRequiredForSignIn,
) {
  return schema.append({
    email: (
      detailsRequiredForSignIn
        ? emailRequired && (visitorTypesDetailsRequired === 'all' || visitorTypesDetailsRequired === 'email')
        : emailRequired
    )
      ? validEmail().messages({
          'string.empty': VALIDATION_MESSAGE.EMAIL_MISSING,
          'string.email': VALIDATION_MESSAGE.EMAIL_MISSING,
        })
      : optionalField(validEmail()),

    mobile: (
      detailsRequiredForSignIn
        ? mobileRequired && (visitorTypesDetailsRequired === 'all' || visitorTypesDetailsRequired === 'mobile')
        : mobileRequired
    )
      ? mobileSchema().messages({
          'string.empty': VALIDATION_MESSAGE.MOBILE_MISSING,
          'string.phoneNumber': VALIDATION_MESSAGE.MOBILE_INCORRECT,
        })
      : optionalField(mobileSchema()),
  });
}

// required or not, true means it is required, false means it is optional
export default function personalInfoSchema({
  emailRequired,
  mobileRequired,
  companyRequired,
  emailOrMobileRequired,
  visitorTypesDetailsRequired,
  detailsRequiredForSignIn,
} = {}) {
  const _personalInfoSchema = Joi.object().keys({
    firstName: firstNameSchema(),
    lastName: lastNameSchema(),
    company: companyRequired ? companySchema() : optionalField(companySchema()),
    companyId: companyIdSchema(),
    companySource: companySourceSchema(),
    site: singleSelectDropdownOption()
      .append({
        countryCode: Joi.string(),
      })
      .required(),
    visitorType: singleSelectDropdownOptionWithVtRequirements().required(),
    host: Joi.optional().allow(''),
    zone: Joi.optional(),
  });

  if (emailOrMobileRequired) {
    return isEmailOrMobileRequired(_personalInfoSchema);
  } else {
    return notEmailOrMobileRequired(
      _personalInfoSchema,
      emailRequired,
      mobileRequired,
      visitorTypesDetailsRequired,
      detailsRequiredForSignIn,
    );
  }
}
